import React, {
  createContext,
  useContext,
  useState,
  type ReactNode,
  type Dispatch,
  type SetStateAction,
} from "react";

interface PageData {
  isiDrawerExpanded?: boolean;
  isiOpenedFromNav?: boolean;
  mobileMenuOpen?: boolean;
  mobileUtilitySublistOpen?: number[];
  isModalOpen?: boolean;
}

type PageDataSetter<K extends keyof PageData> =
  | PageData[K]
  | ((prev: PageData[K]) => PageData[K]);

interface PageContextType {
  data: PageData;
  getData: <K extends keyof PageData>(key: K) => PageData[K];
  setData: <K extends keyof PageData>(key: K, value: PageDataSetter<K>) => void;
  setAll: Dispatch<SetStateAction<PageData>>;
}

const Context = createContext<PageContextType | undefined>(undefined);

export const usePageContext = () => {
  const context = useContext(Context);
  if (!context) {
    throw new Error(
      "usePageContext must be used within a PageContext provider",
    );
  }
  return context;
};

interface PageProviderProps {
  children: ReactNode;
}

export const PageContext = ({ children }: PageProviderProps) => {
  const [pageData, setPageData] = useState<PageData>({
    isiDrawerExpanded: false,
    mobileMenuOpen: false,
    isModalOpen: false,
  });

  const getData = <K extends keyof PageData>(key: K) => pageData[key];

  const setData = <K extends keyof PageData>(
    key: K,
    value: PageDataSetter<K>,
  ) => {
    setPageData((prev) => {
      const nextValue =
        typeof value === "function"
          ? (value as (previous: PageData[K]) => PageData[K])(prev[key])
          : value;

      return {
        ...prev,
        [key]: nextValue,
      };
    });
  };

  return (
    <Context.Provider
      value={{ data: pageData, getData, setData, setAll: setPageData }}
    >
      {children}
    </Context.Provider>
  );
};
